// dependency-graph-ignore-file
import { getBaseUrl } from '@databases/database-state';
import { type MessageFormatter } from '@databases/intl/intl-types';
import { type PanelManager } from '@databases/panel-types';
import { type SelectionsApi } from '@databases/selections-api';
import { type DatabaseAdapter } from '@databases/selections-api-types';
import { type SelectionsActions } from '@databases/selections-api-types';
import { DatabaseFieldType } from '@databases/types/database';
import { getDatabasePath } from '@databases/utils/urls';

import { getExperimentValue } from '@canvas/client-feature-flags';

import { type SelectionsEffectCellContextMenuContext } from '../select-value-menu/types';

export function createSelectValueMenuContext({
  canEditContent,
  canEditFields,
  closeMenu,
  contentId,
  currentViewId,
  databaseAdapter,
  entryId,
  fieldId,
  formatMessage,
  isDBAiFirstCreationEnabled = false,
  panel,
  panelManager,
  selectionsActions,
  selectionsApi,
}: {
  canEditContent: boolean;
  canEditFields: boolean;
  closeMenu: () => void;
  contentId: string;
  currentViewId: string;
  databaseAdapter: DatabaseAdapter;
  entryId: string;
  fieldId: string;
  formatMessage: MessageFormatter;
  isDBAiFirstCreationEnabled?: boolean;
  panel?: boolean;
  panelManager: PanelManager;
  selectionsActions: SelectionsActions;
  selectionsApi: SelectionsApi;
}): SelectionsEffectCellContextMenuContext | null {
  const isSelected = selectionsApi.isValueSelected(entryId, fieldId);

  if (!isSelected) {
    return null;
  }

  const selection = selectionsApi.getResolvedSelection();

  if (selection.mode !== 'cell') {
    return null;
  }

  if (selection.values.length !== 1) {
    return null;
  }

  const resolvedValue = selection.values[0];
  const isDeletionDisabled = databaseAdapter.getAllFieldIds().length === 1;

  const canAskRovo =
    isDBAiFirstCreationEnabled &&
    canEditContent &&
    getExperimentValue('databasesAIFC', 'enabled', {
      fireExperimentExposure: false,
    });
  const canInsertContent = canEditContent;
  const canDeleteFields = canEditFields && !isDeletionDisabled;
  const canOpenComments = true;

  const canEditValue = databaseAdapter.canEditValue(
    resolvedValue.fieldId,
    resolvedValue.entryId
  );

  const canClearContent =
    canInsertContent &&
    canEditValue &&
    resolvedValue.field.type !== DatabaseFieldType.PAGE_STATUS;

  return {
    baseUrl: getBaseUrl(),
    capabilities: {
      canAskRovo,
      canClearContent,
      canDeleteFields,
      canInsertContent,
      canOpenComments,
    },
    closeMenu,
    contentId,
    currentViewId,
    databasePath: getDatabasePath({
      spaceKey: databaseAdapter.getSpaceKey(),
      contentId,
    }),
    entryId,
    fieldId,
    formatMessage,
    panelManager,
    panel,
    selectionsActions,
  };
}
